import {
  IFilterComboComponent,
  FiltersTypeEnum,
  IFilterComboTextSearchComponent
} from "world-archery-lib";
import { BehaviorSubject, Observable, of, combineLatest } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import {
  distinctUntilChanged,
  filter,
  debounceTime,
  map,
  tap,
  mergeMap,
  withLatestFrom
} from "rxjs/operators";
import {
  IFilterTextComponent,
  IFilterRangeSimpleComponent,
  IFilterDateComponent,
  IFilterRangeDateComponent
} from "../../../library/components/components/filters/filters.enum";
import { EntitiesStore } from "../entities-store/entities-store.abstract";
import {
  IAthleteBiography,
  AthleteBiographyStore,
  IAthleteCategories
} from "../entities-store/athlete-biography-store";
import { FormControl } from "@angular/forms";

export type TypesCombos =
  | IFilterComboComponent
  | IFilterComboTextSearchComponent;

/**
 * Provides filter which values are obtained from API calls.
 *
 * @export
 * @class ComboStoreComponent
 * @template T
 */
export class ComboStoreComponent<
  T = any,
  Type = IFilterComboComponent
  > extends EntitiesStore<Array<T>> {
  protected comboInfo: TypesCombos;

  /**
   * Creates an instance of ComboStoreComponent.
   *
   * Here the http request is DONE.
   *
   * @param {string} name_filter
   *   Filter name.
   * @param {string} option_name_property
   *   Property name.
   * @param {string} id
   *   Id of the filter.
   * @param {BehaviorSubject<Array<T>>} [options_subject]
   * @param {BehaviorSubject<T>} [options_selected_subject]
   * @param {HttpClient} [http]
   * @param {string} [endpoint_name]
   *   Endpoint name.
   * @param {Array<{ item: T, position: number }>} [extraOptions]
   *
   * @memberof ComboStoreComponent
   */
  constructor(
    name_filter: string,
    option_name_property: string,
    id: string,
    options_subject: BehaviorSubject<Array<T>>,
    options_selected_subject: BehaviorSubject<T | T[]>,
    protected http: HttpClient,
    protected endpoint_name: string,
    protected extraOptions?: Array<{ item: T; position: number }>,
    dependencies?: Array<{ entity: EntitiesStore<any>; params?: any }>,
    avoidDefaultCall?: boolean,
    intercept_name?: string
  ) {
    super(endpoint_name, http, dependencies);
    this.comboInfo = {
      type: FiltersTypeEnum.ComboBox,
      name_filter: name_filter,
      option_name_property: option_name_property,
      intercept_name: intercept_name,
      id: id,
      options_selected_subject: options_selected_subject
        ? options_selected_subject
        : new BehaviorSubject({}),
      options_subject: options_subject
        ? options_subject
        : new BehaviorSubject([])
    } as any;
    if (avoidDefaultCall !== true) {
      this.requestData({}, false).subscribe();
    }
  }

  /**
   * Get filter info.
   *
   * @readonly
   * @memberof ComboStoreComponent
   */
  get filterInfo() {
    return { options: this.comboInfo };
  }

  /**
   * Get the combo values.
   *
   * @readonly
   * @type {Observable<T>}
   * @memberof ComboStoreComponent
   */
  get comboData(): Observable<T> {
    // tslint:disable-next-line:max-line-length
    return this.comboInfo.options_selected_subject
      .asObservable()
      .pipe(
        filter(
          a =>
            a != undefined &&
            a[this.filterInfo.options.option_name_property] != undefined
        )
      );
  }

  setSelected(data: T | T[]) {
    if (this.filterInfo.options.selectMultiple) {
      if (!(data instanceof Array)) {
        this.filterInfo.options.options_selected_subject.next([data]);
      } else {
        this.filterInfo.options.options_selected_subject.next(data);
      }
    } else {
      this.filterInfo.options.options_selected_subject.next(data);
    }
  }

  /**
   * Set combo values.
   *
   * @param {Array<T>} data
   *   Data.
   * @param {T} default_option
   *   Default option.
   *
   * @memberof ComboStoreComponent
   */
  setData(data: Array<T>, default_option: T | T[]) {
    this.filterInfo.options.options_subject.next(data);
    if (default_option) {
      this.setSelected(default_option);
    } else {
      this.setSelected(data);
    }
  }

  /**
   * A method for obtain combo data and set this propertly
   *
   * @param {*} params
   * @memberof ComboStoreComponent
   */
  protected getRequest(params: any, request_more: boolean = false) {
    if (this.http && this.endpoint_name) {
      return this.http
        .get<{ items: Array<T> }>(environment.api_url, {
          params: new HttpParams({ fromObject: params })
            .append("content", this.endpoint_name)
            .append("v", "3")
        })
        .pipe(
          tap(next => {
            let data = [];
            if (this.extraOptions) {
              this.extraOptions.forEach(value => {
                next.items.splice(value.position, 0, value.item);
              });
            }
            data = next.items;
            this.data = next.items as any;
            this.setData(data, data[0])
            /*if (this.comboInfo.options_subject) {
              this.comboInfo.options_subject.next(data);
              this.comboInfo.options_selected_subject.next(data[0]);
            } else {
              this.comboInfo.options_subject.next(data);
              this.comboInfo.options_selected_subject.next(data[0]);
            }*/
          })
        ) as any;
    } else {
      if (this.extraOptions) {
        const extraOptionMap = this.extraOptions.map(value => value.item);
        this.setData(extraOptionMap, extraOptionMap[0]);
      }
      return of({
        items: this.extraOptions ? this.extraOptions : [],
        pageInfo: {
          page: 1,
          resultsPerPage: this.extraOptions ? this.extraOptions.length : 0,
          totalResults: this.extraOptions ? this.extraOptions.length : 0,
        }
      });
    }
  }
}

export class ComboSearchComponent<T = any> extends ComboStoreComponent<
  T,
  IFilterComboTextSearchComponent
> {
  constructor(
    name_filter: string,
    option_name_property: string,
    id: string,
    options_subject: BehaviorSubject<Array<T>>,
    options_selected_subject: BehaviorSubject<T>,
    protected groupProperty: string = "Categories",
    protected groupKey: string,
    protected http: HttpClient,
    protected endpoint_name: string,
    protected extraOptions?: Array<{ item: T; position: number }>,
    dependencies?: Array<{ entity: EntitiesStore<any>; params?: any }>
  ) {
    super(
      name_filter,
      option_name_property,
      id,
      options_subject,
      options_selected_subject,
      http,
      endpoint_name,
      extraOptions,
      dependencies
    );
    (this.filterInfo.options as any).type = FiltersTypeEnum.ComboTextSearch;
    (this.filterInfo.options as any).groupProperty = groupProperty;
    (this.filterInfo.options as any).groupKey = "group";
  }
  getRequest(params: any, request_more: boolean = false) {
    return super.getRequest(params, request_more).pipe(
      tap(a => {
        let data = [];
        if (this.extraOptions.length > 0) {
          this.extraOptions.forEach(value => {
            (a as any).items.splice(value.position, 0, value.item);
          });
        }
        data = (a as any).items;
        const tempArr = data.reduce((r, e) => {
          // get first letter of name of current element
          let group = e[this.filterInfo.options.option_name_property][0];
          // if there is no property in accumulator with this letter create it
          if (!r[group])
            r[group] = {
              group,
              [this.groupProperty]: [
                Object.assign(e, {
                  DNameIn: e[this.filterInfo.options.option_name_property]
                })
              ]
            };
          // if there is push current element to children array for that letter
          else
            r[group][this.groupProperty].push(
              Object.assign(e, {
                DNameIn: e[this.filterInfo.options.option_name_property]
              })
            );
          // return accumulator
          return r;
        }, {});
        this.data = Object.values(tempArr);
        if (this.comboInfo.options_subject) {
          this.comboInfo.options_subject.next(this.data);
          this.comboInfo.options_selected_subject.next(this.data[0]);
        } else {
          this.comboInfo.options_subject.next(this.data);
          this.comboInfo.options_selected_subject.next(this.data[0]);
        }
      })
    );
  }
}

/**
 * Filter by text.
 *
 * @export
 * @class TextFilter
 */
export class TextFilter {
  private comboInfo: IFilterTextComponent;

  /**
   * Creates an instance of TextFilter.
   *
   * @param {string} id
   *   Filter input id.
   * @param {BehaviorSubject<string>} [text_subject]
   * @param {string} [default_value]
   *   Default value.
   * @memberof TextFilter
   */
  constructor(
    id: string,
    text_subject?: BehaviorSubject<string>,
    default_value?: string,
    intercept_name?: string
  ) {
    this.comboInfo = {
      type: FiltersTypeEnum.TextSearch,
      id: id,
      defaultTextValue: default_value ? default_value : "",
      text_subject: text_subject ? text_subject : new BehaviorSubject(""),
      intercept_name: 'search'
    };
  }

  /**
   * Get info about fitlers.
   *
   * @readonly
   * @memberof TextFilter
   */
  get filterInfo() {
    return { options: this.comboInfo };
  }

  /**
   * Get the text we are filtering for.
   *
   * @param {number} [debounce]
   *   Time to debounce.
   *
   * @returns {Observable<{ text: string }>}
   * @memberof TextFilter
   */
  data(debounce?: number): Observable<{ text: string }> {
    return this.comboInfo.text_subject.asObservable().pipe(
      debounceTime(debounce ? debounce : 300),
      map(a => {
        return { text: a };
      })
    );
  }

  /**
   * Set the text.
   *
   * @param {string} data
   *   Text.
   * @memberof TextFilter
   */
  setData(data: string) {
    this.comboInfo.text_subject.next(data);
  }
}

/**
 * Region interface for using in combo that comes from api
 *
 * @export
 * @interface IRegionComboData
 */
export interface IRegionComboData {
  CaId: number;
  CaCode: string;
  CaName: string;
  Continent: string;
}

/**
 * Combo with the list of continents.
 *
 * @export
 * @class RegionCombo
 * @extends {ComboStoreComponent<IRegionComboData>}
 */
export class RegionCombo extends ComboStoreComponent<IRegionComboData> {
  constructor(
    name_filter,
    id,
    http,
    sharedOption: BehaviorSubject<Partial<IRegionComboData>>,
    extraOptions?: Array<any>,
    initial_state?:IRegionComboData
  ) {
    super(
      name_filter,
      "Continent",
      id,
      undefined,
      sharedOption as any,
      http,
      "CONTINENTALASSOCIATIONS",
      extraOptions,
      [],
      undefined,
      'continent'
    );
    if(initial_state){
      this.filterInfo.options.initial_state = true;
      this.filterInfo.options.options_selected_subject.next(undefined);

    }
  }
}

/**
 * Region interface for using in combo that comes from api.
 *
 * @export
 * @interface ICountryComboData
 */
export interface ICountryComboData {
  NOC: string;
  CName: string;
  CShort: string;
}

/**
 * A Country combo.
 *
 * @export
 * @class CountryCombo
 * @extends {ComboStoreComponent<ICountryComboData>}
 */
export class CountryCombo extends ComboStoreComponent<ICountryComboData> {
  constructor(name_filter, id, http, extraOptions?: Array<any>, intercept_name: string = 'country',
    allow_multiple: boolean = false, withTextSearch = true
  ) {
    super(
      name_filter,
      "CShort",
      id,
      undefined,
      undefined,
      http,
      "COUNTRIES",
      extraOptions,
      [],
      undefined,
      'country',
    );
    this.requestData({ RBP: "ALL" }, false).subscribe();
    this.filterInfo.options.withTextSearch = withTextSearch;
    this.filterInfo.options.selectMultiple = allow_multiple;

    this.filterInfo.options.renderFunc = (value) => {
      return `
      <div style="display:flex;    justify-content: flex-end;">
      <div class="">

      ${value.CShort}
    </div>
      <div style="margin-left:10px; class="country__img" >
      <div style="margin-left:10px; width:20px; height:20px;">
      ${value.NOC ? `<img style="width: 20px;
      height: 20px;" src="//extranet.worldarchery.sport/Flags/${value.NOC}.svg" />` : ''}
      </div>

  </div>
  </div>` as any;
    }
    if (allow_multiple) {
      this.filterInfo.options.renderFuncSelected = (value) => {
        value = typeof value === 'string' ? JSON.parse(value) : value;
        console.log(value);
        if (value && value.filter(country => country.NOC).length > 0) {
          return value.filter(country => country.NOC).map(country => `<img style="width: 20px;
          height: 20px; margin-left:10px; margin-right:10px" src="//extranet.worldarchery.sport/Flags/${country.NOC}.svg" />`).join('');
        }
        return this.data && this.data instanceof Array ? (this.data[0] as any).CShort : 'All';
      }
    } else {

      this.filterInfo.options.renderFuncSelected =  (value) => {
        value = typeof value === 'string' ? JSON.parse(value) : value;

        return `
        <div style="display:flex;    justify-content: flex-end; align-items:center;">
        <div class="">

        ${value.CShort}
      </div>
      ${value.NOC ? `<div class="country__img" >
        <div style="margin-left:10px; width:20px; height:20px;">
        <img style="width: 20px;
        height: 20px;" src="//extranet.worldarchery.sport/Flags/${value.NOC}.svg" />` : ''}
        </div>

    </div>
    </div>` as any;
      }

    }

  }

  /**
   * {@inheritdoc}
   */
  getRequest(params: any, request_more = false) {
    return super.getRequest(params, request_more).pipe(tap(a => {
      // Sort countries.
      this.data = this.data.sort((f, s) => f['CShort'].toLowerCase() > s['CShort'].toLowerCase() ? 1 : -1);
      if (this.extraOptions) {
        this.extraOptions.forEach(value => {
          const index = this.data.findIndex((item) => item['CShort'] === value.item['CShort']);
          index > -1 ? this.data.splice(index, 1) : undefined;
          this.data.splice(value.position, 0, value.item as any);
        });
      }
      this.setData(this.data as any, this.data[0]);
      this.dataStream.next(this.data);
    }));
  }

  get comboData() {
    if (this.comboInfo.selectMultiple) {


      return this.comboInfo.options_selected_subject.asObservable().pipe(
        map(a => {
          return { NOC: (a as Array<any>).filter(value => value.NOC).map(value => value.NOC).join('|') } as ICountryComboData;
        })
      )
    } else {
      return super.comboData;
    }
  }
}

/**
 * Interface for historical combo of athletes in federation page.
 *
 * @export
 * @interface IHistoricalAthleteCombo
 */
export interface IHistoricalAthleteCombo {
  Name: string;
  Value: boolean;
}

/**
 * Historical and active combo used for define that if an athlete is active or not.
 *
 * @export
 * @class HistoricalComboAthlete
 * @extends {ComboStoreComponent<IHistoricalAthleteCombo>}
 */
export class HistoricalComboAthlete extends ComboStoreComponent<
  IHistoricalAthleteCombo
> {
  constructor(name_filter, id, http, extraOptions?: Array<any>) {
    super(
      name_filter,
      "Name",
      id,
      undefined,
      undefined,
      undefined,
      undefined,
      extraOptions,
      [],
      undefined,
      'historic'
    );
  }
}

/**
 * Interface for category combo
 *
 * @export
 * @interface ICategoryCombo
 */
export interface ICategoryCombo {
  Id: number;
  Code: string;
  Name: string;
  ParaAr: boolean;
  His: boolean;
  DivId: number[];
  DivisionLabel: string;
  PlayersLabel: string;
  Mixed: boolean;
  Gender: string[];
}

/**
 * Category combo store
 *
 * @export
 * @class CategoryCombo
 * @extends {ComboStoreComponent<ICategoryCombo>}
 */
export class CategoryCombo extends ComboStoreComponent<ICategoryCombo> {
  constructor(name_filter, id, http, extraOptions?: Array<any>) {
    super(
      name_filter,
      "Name",
      id,
      undefined,
      undefined,
      http,
      "CATEGORIES",
      extraOptions
    );
    this.requestData({ RBP: "ALL" }, false).subscribe();
  }
}

/**
 * Category search combo
 *
 * @export
 * @class CategorySearchCombo
 * @extends {ComboSearchComponent<ICategoryCombo>}
 */
export class CategorySearchCombo extends ComboSearchComponent<ICategoryCombo> {
  constructor(name_filter, id, http, extraOptions?: Array<any>) {
    super(
      name_filter,
      "Name",
      id,
      undefined,
      undefined,
      "Categories",
      "Name",
      http,
      "CATEGORIES",
      extraOptions
    );
    this.requestData({ RBP: "ALL" }, false).subscribe();
  }
}

/**
 * Interface for yes and not combo
 *
 * @export
 * @interface IYesNoCombo
 */
export interface IYesNoCombo {
  Name: string;
  Value: boolean;
}

/**
 * Combo for say yes or not
 *
 * @export
 * @class HistoricalComboAthlete
 * @extends {ComboStoreComponent<IYesNoCombo>}
 */
export class YesNotCombo extends ComboStoreComponent<IYesNoCombo> {
  constructor(name_filter, id, initialState?: IYesNoCombo) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "Yes", Value: true }, position: 0 },
      { item: { Name: "No", Value: false }, position: 1 }
    ]);
    if (initialState) {
      this.comboInfo.initial_state = true;
      this.comboInfo.options_selected_subject.next(initialState);
    }
  }
}

/**
 * Combo for set the category of athletes
 *
 * @export
 * @class AthleteCategoriesCombo
 * @extends {ComboStoreComponent<IAthletesCategories>}
 */
export class AthleteCategoriesCombo extends ComboStoreComponent<
  IAthleteCategories
> {
  /**
   *Creates an instance of AthleteCategoriesCombo.
   * @param {*} name_filter the name of the filter.
   * @param {*} id the id of the filter.
   * @param {Array<{ item: IAthleteCategories; position: number }>} extraOptions extra options if any.
   * @param {HttpClient} http The http service.
   * @param {boolean} filterTeam if the categories of the combo should be filtered by team or not.
   * @memberof AthleteCategoriesCombo
   */
  constructor(
    name_filter,
    id,
    extraOptions: Array<{ item: IAthleteCategories; position: number }>,
    http: HttpClient,
    public filterTeam?,
  ) {
    // tslint:disable-next-line:max-line-length
    super(
      name_filter,
      "CatName",
      id,
      undefined,
      undefined,
      http,
      undefined,
      extraOptions,
      [{ entity: new CategoryCombo("", "", http), params: { RBP: "ALL" } }],
      undefined,
      'category'
,    );
    this.comboInfo.initial_state = true;
  }

  /**
   *
   * @inheritdoc
   * @returns
   * @memberof AthleteCategoriesCombo
   */
  getRequest() {
    return super.getRequest({}, false).pipe(
      withLatestFrom(
        (this.dependencies[0]
          .entity as CategoryCombo).filterInfo.options.options_subject.asObservable()
      ),
      map(([a, b]) => {
        let tmpArr = Array.from(a.items);
        tmpArr = tmpArr.map(category => {
          // tslint:disable-next-line:max-line-length
          const item = (this.dependencies[0].entity as CategoryCombo).data.find(
            (value: any) => value.Id.toString() === (category as any).item.CatId.toString()
          );
          (category as any).item.CatName = (item as any).Name;
          Object.assign((category as any).item, item)
          return (category as any).item;
        });
        if (this.filterTeam != undefined) {
          tmpArr = tmpArr.filter(value => (value as any).isTeam === this.filterTeam);
        }

        tmpArr = Array.from((tmpArr.reduce((f: Map<string, any>, s: any) => f.set(s.Id, s), new Map()) as any).values());
        const categoryDefault = tmpArr.find(value => ['CMO', 'CWO', 'RMO', 'RWO', 'W1M', 'W1W']
          .findIndex(cat => cat === (value as any).Cat) > -1);
        this.filterInfo.options.options_subject.next(tmpArr);
        this.filterInfo.options.options_selected_subject.next(categoryDefault ? categoryDefault : tmpArr[0]);
        this.filterInfo.options.options_selected_subject.asObservable().subscribe(next => {
        });
        this.data = tmpArr as any;
        this.loadingRef = false;
        return tmpArr;
      })
    );
  }
}

export class DateFilterStore {
  dateComboInfo: IFilterDateComponent;

  /**
   *Creates an instance of DateFilterStore.
   * @param {string} id the id to be assigned into the dom.
   * @param {string} placeholder The placeholder text.
   * @param {string} [date_format="dd MMMM yyyy"] The date format.
   * @param {FormControl} [dateFormControl] An optional form control.
   * @param {BehaviorSubject<{ value: Date }>} [date_subject] An Optional date subject for allow components to share date.
   * @param {Date} [min_date] The min date for validation.
   * @param {Date} [max_date] The max date for validation.
   * @memberof DateFilterStore
   */
  constructor(
    id: string,
    placeholder: string,
    date_format: string = "dd MMMM yyyy",
    dateFormControl?: FormControl,
    date_subject?: BehaviorSubject<{ value: Date }>,
    min_date?: Date,
    max_date?: Date,
    intercept_name?: string
  ) {
    this.dateComboInfo = {
      type: FiltersTypeEnum.DateFilter,
      id: id,
      dateFormControl: dateFormControl,
      placeholder: placeholder,
      date_subject: date_subject
        ? date_subject
        : new BehaviorSubject<any>({ value: new Date() }),
      date_format: date_format,
      min_date: min_date,
      max_date: max_date,
      intercept_name: intercept_name
    };
  }
  /**
   * Get info about fitlers.
   *
   * @readonly
   * @memberof DateFilterStore
   */
  get filterInfo() {
    return { options: this.dateComboInfo };
  }

  get data(): Observable<{ value: Date }> {
    return this.dateComboInfo.date_subject.asObservable().pipe(
      map(a => {
        return { value: a };
      })
    );
  }

  /**
   * Set the date.
   *
   * @param {Date} data
   *
   * @memberof setData
   */
  setData(data: Date) {
    this.dateComboInfo.date_subject.next({ value: data } as any);
  }
}

export class RangeDateFilterStore {
  dateRangeInfo: IFilterRangeSimpleComponent;
  constructor(
    id: string,
    date_from?: DateFilterStore,
    date_to?: DateFilterStore,
    hideFilter?: boolean,
    date_format?: string,
    form_control_to?: FormControl,
    form_control_from?: FormControl
  ) {
    this.dateRangeInfo = {
      hideFilter: hideFilter ? hideFilter : false,
      type: FiltersTypeEnum.RangeDateSimpleFilter,
      // tslint:disable-next-line:max-line-length
      date_from: date_from
        ? date_from.filterInfo.options
        : new DateFilterStore(
          "from-" + id,
          "From: ",
          undefined,
          form_control_from,
          undefined,
        ).filterInfo.options,
      // tslint:disable-next-line:max-line-length
      date_to: date_to
        ? date_to.filterInfo.options
        : new DateFilterStore(
          "to-" + id,
          "To: ",
          undefined,
          form_control_to,
          undefined
        ).filterInfo.options
    };
  }
  /**
   * Get info about fitlers.
   *
   * @readonly
   * @memberof RangeDateFilterStore
   */
  get filterInfo() {
    return { options: this.dateRangeInfo };
  }

  /**
   * Get the dates we are filtering for.
   *
   *
   * @returns Observable<{ date_from: Date, date_to:Date }>
   * @memberof RangeDateFilterStore
   */
  get data(): Observable<{ date_from: Date; date_to: Date }> {
    // tslint:disable-next-line:max-line-length
    return combineLatest(
      this.dateRangeInfo.date_from.date_subject.asObservable(),
      this.dateRangeInfo.date_to.date_subject.asObservable()
    ).pipe(
      map(([date_from, date_to]) => {
        return {
          date_from: (date_from as any).value as any,
          date_to: (date_to as any).value as any
        };
      })
    );
  }

  /**
   * Get the dates we are filtering for.
   *
   *
   * @returns Observable<{ date_from: Date, date_to:Date }>
   * @memberof RangeDateFilterStore
   */
  get comboData(): Observable<{ date_from: Date; date_to: Date }> {
    // tslint:disable-next-line:max-line-length
    return combineLatest(
      this.dateRangeInfo.date_from.date_subject.asObservable(),
      this.dateRangeInfo.date_to.date_subject.asObservable()
    ).pipe(
      map(([date_from, date_to]) => {
        return {
          date_from: (date_from as any).value as any,
          date_to: (date_to as any).value as any
        };
      })
    );
  }

  /**
   * A method for set date into the date combos
   *
   * @param {Date} date_from date from
   * @param {Date} date_to date to
   * @memberof RangeDateFilterStore
   */
  setDates(date_from: Date, date_to: Date) {
    this.filterInfo.options.date_to.date_subject.next({
      value: date_to
    } as any);
    this.filterInfo.options.date_from.date_subject.next({
      value: date_from
    } as any);
  }
}

/**
 * Interface for win/lost combo
 *
 * @export
 * @interface IYesNoCombo
 */
export interface IWinLoseCombo {
  Name: string;
  Value: string;
}

/**
 * Combo for return win or lose
 *
 * @export
 * @class WinLoseCombo
 * @extends {ComboStoreComponent<IWinLoseCombo>}
 */
export class WinLoseCombo extends ComboStoreComponent<IWinLoseCombo> {
  constructor(name_filter, id) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "All", Value: "all" }, position: 0 },
      { item: { Name: "Win", Value: "win" }, position: 1 },
      { item: { Name: "Lost", Value: "lost" }, position: 2 }
    ]);
  }
}

/**
 * Interface for individual, mixed team and Team combo in athlete results page
 *
 * @export
 * @interface ITeamResultsAthleteCombo
 */
export interface ITeamResultsAthleteCombo {
  Name: string;
  Value: number;
}

/**
 * Combo for individual, mixed team and Team combo in athlete results page
 *
 * @export
 * @class EventAthleteResultsCombo
 * @extends {ComboStoreComponent<EventAthleteResultsCombo>}
 */
export class EventAthleteResultsCombo extends ComboStoreComponent<
  ITeamResultsAthleteCombo
> {
  constructor(name_filter, id) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "Individual", Value: 1 }, position: 1 },
      { item: { Name: "Mixed Team", Value: 3 }, position: 2 },
      { item: { Name: "Team", Value: 4 }, position: 2 }
    ]);
  }
}

/**
 * Interface for the records status of an athlete
 *
 * @export
 * @interface IStatusRecordAthleteCombo
 */
export interface IStatusRecordAthleteCombo {
  Name: string;
  Value: number;
}

/**
 * Combo for the records status (All/standing/broken) of an athlete
 *
 * @export
 * @class StatusAthleteRecordsCombo
 * @extends {ComboStoreComponent<StatusAthleteRecordsCombo>}
 */
export class StatusAthleteRecordsCombo extends ComboStoreComponent<
  IStatusRecordAthleteCombo
> {
  constructor(name_filter, id) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "All", Value: 1 }, position: 0 },
      { item: { Name: "Standing", Value: 0 }, position: 1 },
      { item: { Name: "Broken", Value: 2 }, position: 2 }
    ]);
  }
}

/**
 * Interface for the records status of an athlete
 *
 * @export
 * @interface IHistoricalRecordAthleteCombo
 */
export interface IHistoricalRecordAthleteCombo {
  Name: string;
  Value: number;
}

/**
 * Combo for the records status (All/standing/broken) of an athlete
 *
 * @export
 * @class HistoricalRecordsAthleteCombo
 * @extends {ComboStoreComponent<HistoricalRecordsAthleteCombo>}
 */
export class HistoricalRecordsAthleteCombo extends ComboStoreComponent<
  IHistoricalRecordAthleteCombo
> {
  constructor(name_filter, id) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "Current", Value: 0 }, position: 0 },
      { item: { Name: "Historic", Value: 1 }, position: 1 }
    ]);
  }
}

/**
 * Interface for the records status of an athlete
 *
 * @export
 * @interface ILevelRecordAthleteCombo
 */
export interface ILevelRecordAthleteCombo {
  RecordLevelId: string;
  Description: string;
}

/**
 * Combo for the records status (All/standing/broken) of an athlete
 *
 * @export
 * @class LevelRecordsAthleteCombo
 * @extends {ComboStoreComponent<LevelRecordsAthleteCombo>}
 */
export class LevelRecordsAthleteCombo extends ComboStoreComponent<
  ILevelRecordAthleteCombo
> {
  constructor(name_filter, id, http: HttpClient) {
    super(
      name_filter,
      "Description",
      id,
      undefined,
      undefined,
      http,
      "RECORDLEVELS",
      []
    );
  }
  protected getRequest(params: any, request_more: boolean = false) {
    return super.getRequest(params, request_more).pipe(
      map((next: any) => {
        next.items = next.items
          .filter(value => ["1", "2"].includes(value.RecordLevelId))
          .reverse();
        this.data = next.items as any;
        this.filterInfo.options.options_subject.next(this.data);
        this.filterInfo.options.options_selected_subject.next(this.data[0]);
        return next;
      })
    );
  }
}


/**
 * Combo for individual and Team combo in competition rankings page
 *
 * @export
 * @class CompetitionRankingIndividualTeamCombo
 * @extends {ComboStoreComponent<EventAthleteResultsCombo>}
 */
 export class CompetitionRankingIndividualTeamCombo extends ComboStoreComponent<
 ITeamResultsAthleteCombo
> {
 constructor(name_filter, id) {
   super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
     { item: { Name: "Individual", Value: 1 }, position: 1 },
     { item: { Name: "Team", Value: 4 }, position: 2 }
   ]);
 }
}


/**
 * Combo used in athlete statistics page to set Outdoor or Indoor event discipline.
 *
 * @export
 * @class AthleteDisciplineStatisticsCombo
 * @extends {AthleteDisciplineStatisticsCombo<IYesNoCombo>}
 */
 export class AthleteDisciplineStatisticsCombo extends ComboStoreComponent<IYesNoCombo> {
  constructor(name_filter, id, initialState?: IYesNoCombo) {
    super(name_filter, "Name", id, undefined, undefined, undefined, undefined, [
      { item: { Name: "Outdoor", Value: false }, position: 0 },
      { item: { Name: "Indoor", Value: false }, position: 1 }
    ]);
    this.comboInfo.intercept_name = 'discipline_stats';
    this.comboInfo.delay_time = 0;
    if (initialState) {
      this.comboInfo.initial_state = true;
      this.comboInfo.options_selected_subject.next(initialState);
    }
  }
}